import React, {useState, useEffect, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    TextField,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    MenuItem, Snackbar,
} from '@mui/material';
import { styled } from '@mui/system';
import * as MyStyles from "../../../styles/MyStyles";
import {
    StyledButtonOrange,
    StyledButtonContainer,
    StyledTableCell,
    StyledTabPanel,
    StyledTableContainer, StyledTable, StyledTableHeadRow, StyledTableCellHeader, StyledTableBodyRow, StyledAlert
} from "../../../styles/MyStyles";
import {GetCookieInfo} from "../../../logic/CookieManager";
import {GetEnumValueName, GetEnumMenuItems} from "../../../logic/EnumHelper";
import dayjs from "dayjs";
import {GetLookupValue, GetLookupValueName} from "../../../logic/LookupHelper";
import {GetLastTab, RememberTab} from "../../../logic/ClientTabReminder";
import {
    GetCryptoAmountExecutedAsSting,
    GetCryptoAmountRequestedAsSting, GetExecutedPricesSting,
    GetRequestedPricesSting
} from "../../../logic/OrderVolumeHelper";
import {GetBrokerageAgreementTitle, GetOrderTitle} from "../../../logic/EntityTitleHelper";
import {GetClient, GetClients, UpsertClientCache} from "../../../logic/DataProvider";
import {GetClientViewName} from "../../../logic/ClientNameHelper";
import ManagerFilesPage from "../../files/ManagerFilesPage";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AmlPage from "./AmlPage";
import * as ClientDetailsHelper from "../../../logic/ClientDetailsHelper";
import * as TransferHelper from "../../../logic/TrasferHelper";
import * as ConfirmationDownloader from "../../../logic/ConfirmationDownloader";
import BalanceComponent from "../../balance/BalanceComponent";
import * as DataProvider from "../../../logic/DataProvider";

const StyledTabs = styled(Tabs)`
  && {
    color: ${MyStyles.White};
    font-weight: bold;
  }
`;

const StyledTab = styled(Tab)`
  &.Mui-disabled {
    color: ${MyStyles.Gray};
  }
  & {
    font-weight: bold;
    color: ${MyStyles.Black};
  }
  &.Mui-selected {
    color: ${MyStyles.Black};
  }
`;

const ClientPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('clientDetails');
    const [editMode, setEditMode] = useState(clientId === '0');
    const [clientDetails, setClientDetails] = useState( clientId === '0' ? {
        'type': 0,
        'email': '',
        'dossierId': '',
        'privateMetadata': {
            firstName: '',
            lastName: '',
            residenceAddress: '',
            businessActivity: '',
            nationalityId: '',
            residenceCountryId: '',
            dateOfBirth: '',
        },
        'legalMetadata': {
            name: '',
            registrationCountryId: '',
            registrationAddress: '',
            type: '',
            businessActivity: '',
            numberOfEmployees: '',
            form: ''
        },
        'highRisk': false
    } : {});
    const [clients, setClients] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const [limits, setLimits] = useState([]);
    const [companyMembers, setCompanyMembers] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [trades, setTrades] = useState([]);
    const [chainalysisAlerts, setChainalysisAlerts] = useState([]);
    const [errors, setErrors] = useState({});
    const filesComponentRef = useRef(null);
    const amlComponentRef = useRef(null);
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        vertical: 'top',
        horizontal: 'center',
        message: ''
    });

    const handleGoBack = () => {
        if (clientId !== '0') {
            DataProvider.fetchClient(clientId).then(() => navigate('/contacts/clients'));
        } else {
            navigate('/contacts/clients'); // Go back to the previous page
        }
    };

    const handleTabChange = (event, newTab) => {
        setActiveTab(newTab);
        RememberTab(clientId, newTab);
    };

    const handleEditModeToggle = () => {
        setEditMode((prevEditMode) => !prevEditMode);
    };

    const handleSave = async () => {

        const newErrors = ClientDetailsHelper.ValidateForm(clientDetails);

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (clientDetails.type === 0) {
            clientDetails.legalMetadata = null;
        } else {
            clientDetails.privateMetadata = null;
        }

        console.log(clientDetails);
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_CONTACTS_URL}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                    body: JSON.stringify(clientDetails),
                }
            );
            if (response.ok) {
                const data = await response.json();
                UpsertClientCache(data.data);
                navigate('/contacts/clients/' + data.data.id);
                console.log('Client updated successfully.');
            } else {
                console.log('Update client failed.');
            }
            setEditMode(false);
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const handleCancelEdit = () => {
        if (clientId !== '0') {
            fetchClientDetails(() => setEditMode(false), false);
        } else {
            handleGoBack();
        }
    };

    const handleClientDetailsChange = (field, value) => {
        if (field.includes('.')) {
            let fieldSplitted = field.split('.');
            let firstLevel = fieldSplitted[0];
            let secondLevel = fieldSplitted[1];
            let firstLevelValues = clientDetails[firstLevel];

            firstLevelValues[secondLevel] = value;

            setClientDetails({
                ...clientDetails,
                [firstLevel]: firstLevelValues,
            });
        } else {
            setClientDetails({
                ...clientDetails,
                [field]: value,
            });
        }
    };

    const fetchClientDetails = async (callback, useCache = true) => {
        try {
            if (clientId !== '0') {

                GetClient(clientId, useCache).then((client) => setClientDetails(client));
                if (callback) {
                    callback();
                }

            } else {
                // Create mode, initialize asset with empty values
                setClientDetails({
                    name: '',
                    type: 0,
                    id: 0
                });
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchAgreements = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_AGREEMENTS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setAgreements(data.data);
            } else {
                console.log('Fetch agreements failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchLimits = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_LIMITS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setLimits(data.data);
            } else {
                console.log('Fetch limits failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchCompanyMembers = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_COMPANY_MEMBERS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setCompanyMembers(data.data);
            } else {
                console.log('Fetch company members failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchWallets = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_WALLETS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setWallets(data.data);
            } else {
                console.log('Fetch wallets failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_BANK_ACCOUNTS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setBankAccounts(data.data);
            } else {
                console.log('Fetch bank accounts failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchTransfers = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_TRANSFERS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setTransfers(data.data);
            } else {
                console.log('Fetch transfers failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchOrders = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_ORDERS_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);

                data.data?.sort((a, b) => {
                    const dateA = new Date(a.createdDate);
                    const dateB = new Date(b.createdDate);
                    return dateB - dateA;
                });

                setOrders(data.data);
            } else {
                console.log('Fetch orders failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchTrades = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_TRADES_URL}/${clientId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setTrades(data.data);
            } else {
                console.log('Fetch trades failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    const fetchChainalysisAlerts = async () => {
        try {
            const tokenCookie = GetCookieInfo('nplt');
            const response = await fetch(
                `${process.env.REACT_APP_CHAINALYSIS_URL}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + tokenCookie.value,
                    },
                    body: JSON.stringify({
                        contactId: clientId
                    })
                }
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data.data);
                setChainalysisAlerts(data.data);
            } else {
                console.log('Fetch chainalysis alerts failed.');
            }
        } catch (error) {
            console.log('An error occurred:', error);
        }
    };

    useEffect(() => {
        if (clientId !== '0') {
            fetchClientDetails();
            GetClients().then((clients) => setClients(clients));
            fetchAgreements();
            fetchLimits();
            fetchCompanyMembers();
            fetchWallets();
            fetchBankAccounts();
            fetchTransfers();
            fetchOrders();
            fetchTrades();
            fetchChainalysisAlerts();
        }
        let lastTab = GetLastTab(clientId);
        if (lastTab) {
            setActiveTab(lastTab);
        }
    }, []);

    const createAgreement = () => {
        navigate(`agreement`);
    }

    const createLimit = () => {
        navigate(`limit`);
    }

    const createCompanyMember = () => {
        navigate(`company-member`);
    }

    const createWallet = () => {
        navigate(`wallet`);
    }

    const createBankAccount = () => {
        navigate(`bank-account`);
    }

    const createTransfer = () => {
        navigate(`transfer`);
    }

    const createOrder = () => {
        navigate(`order`);
    }

    const createTrade = () => {
        navigate(`trade`);
    }

    const editAgreement = (agreementId) => {
        navigate(`agreement/${agreementId}`);
    }

    const editLimit = (limitId) => {
        navigate(`limit/${limitId}`);
    }

    const editCompanyMember = (memberId) => {
        navigate(`company-member/${memberId}`);
    }

    const editWallet = (walletId) => {
        navigate(`wallet/${walletId}`);
    }

    const editBankAccount = (accountId) => {
        navigate(`bank-account/${accountId}`);
    }

    const editTransfer = (transferId) => {
        navigate(`transfer/${transferId}`);
    }

    const editOrder = (orderId) => {
        navigate(`order/${orderId}`);
    }

    const editTrade = (tradeId) => {
        navigate(`trade/${tradeId}`);
    }

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleUploadFileClick = () => {
        if (filesComponentRef.current) {
            filesComponentRef.current.handleUploadFileClick();
        }
    };
    const handleDownloadBucketClick = () => {
        if (filesComponentRef.current) {
            filesComponentRef.current.handleDownloadBucketClick();
        }
    };

    const handleNewCheckClick = () => {
        if (amlComponentRef.current) {
            amlComponentRef.current.handleNewCheck();
        }
    };

    const handleRefreshChecksClick = () => {
        if (amlComponentRef.current) {
            amlComponentRef.current.handleRefreshChecksClick();
        }
    };

    const handleRefreshChainalysisClick = () => {
        fetchChainalysisAlerts();
    };

    return (
        <Container sx={{ margin: '0.2rem', minWidth: '99%' }}>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}>
                <StyledAlert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {snackbar.message}
                </StyledAlert>
            </Snackbar>
            <StyledTabs
                variant="scrollable"
                scrollButtons={false}
                allowScrollButtonsMobile
                value={activeTab}
                onChange={handleTabChange} TabIndicatorProps={{
                style: {
                    backgroundColor: MyStyles.Black
                }
            }}>
                <StyledTab label="Client Details" value="clientDetails" />
                <StyledTab label="Agreements" value="agreements" disabled={clientId === '0'} />
                <StyledTab label="Limits" value="limits" disabled={clientId === '0'} />
                {clientDetails.type === 1 && (
                    <StyledTab label="Members" value="company-members" disabled={clientId === '0'} />
                )}
                <StyledTab label="Wallets" value="wallets" disabled={clientId === '0'} />
                <StyledTab label="Bank Accounts" value="bankAccounts" disabled={clientId === '0'} />
                <StyledTab label="Transfers" value="transfers" disabled={clientId === '0'} />
                <StyledTab label="Orders" value="orders" disabled={clientId === '0'} />
                <StyledTab label="Trades" value="trades" disabled={clientId === '0'} />
                <StyledTab label="Files" value="files" disabled={clientId === '0'} />
                <StyledTab label="Aml" value="aml" disabled={clientId === '0'} />
                <StyledTab label="Chainalysis" value="chainalysis" disabled={clientId === '0'} />
                <StyledTab label="Balance" value="balance" disabled={clientId === '0'} />
            </StyledTabs>
            <StyledButtonContainer container>
                <Grid>
                    <StyledButtonOrange variant="contained" onClick={handleGoBack}>
                        Back
                    </StyledButtonOrange>
                </Grid>
                {activeTab === 'files' && (
                    <>
                        <Grid>
                            <StyledButtonOrange variant="contained" onClick={handleUploadFileClick}>
                                Upload <CloudUploadIcon style={{ marginLeft: '0.5em' }}/>
                            </StyledButtonOrange>
                        </Grid>
                        <Grid>
                            <StyledButtonOrange variant="contained" onClick={handleDownloadBucketClick}>
                                Download  <CloudDownloadIcon style={{ marginLeft: '0.5em' }}/>
                            </StyledButtonOrange>
                        </Grid>
                    </>
                )}
                {activeTab === 'aml' && (
                    <>
                        <Grid>
                            <StyledButtonOrange variant="contained" onClick={handleNewCheckClick}>
                                New
                            </StyledButtonOrange>
                        </Grid>
                        <Grid>
                            <StyledButtonOrange variant="contained" onClick={handleRefreshChecksClick}>
                                Refresh
                            </StyledButtonOrange>
                        </Grid>
                    </>
                )}
                {activeTab === 'chainalysis' && (
                    <Grid>
                        <StyledButtonOrange variant="contained" onClick={handleRefreshChainalysisClick}>
                            Refresh
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'clientDetails' && (
                    <>
                        {!editMode ? (
                            <Grid item>
                                <StyledButtonOrange variant="contained" onClick={handleEditModeToggle}>
                                    Edit
                                </StyledButtonOrange>
                            </Grid>
                        ) : (
                            <>
                                <Grid item>
                                    <StyledButtonOrange variant="contained" onClick={handleSave}>
                                        Save
                                    </StyledButtonOrange>
                                </Grid>
                                <Grid item>
                                    <StyledButtonOrange variant="contained" onClick={handleCancelEdit}>
                                        Cancel
                                    </StyledButtonOrange>
                                </Grid>
                            </>
                        )}
                    </>
                )}
                {activeTab === 'agreements' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createAgreement}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'limits' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createLimit}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'company-members' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createCompanyMember}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'wallets' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createWallet}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'bankAccounts' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createBankAccount}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'transfers' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createTransfer}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'orders' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createOrder}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
                {activeTab === 'trades' && (
                    <Grid item>
                        <StyledButtonOrange variant="contained" onClick={createTrade}>
                            New
                        </StyledButtonOrange>
                    </Grid>
                )}
            </StyledButtonContainer>
            <StyledTabPanel value={activeTab} index="clientDetails">
                <Table>
                    <TableBody>
                        <TableRow>
                            <StyledTableCell style={{ width: '10%' }}>Type</StyledTableCell>
                            <TableCell style={{ width: '90%' }}>
                                {editMode ? (
                                    <TextField
                                        name="type"
                                        label="Type"
                                        select
                                        value={clientDetails.type}
                                        onChange={(e) => handleClientDetailsChange('type', e.target.value)}
                                        fullWidth
                                        required
                                        disabled={clientId !== '0'}
                                        error={!!errors.type}
                                        helperText={errors.type}
                                    >
                                        {GetEnumMenuItems('clientType')}
                                    </TextField>
                                ) : (
                                    GetEnumValueName('clientType', clientDetails.type)
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell style={{ width: '10%' }}>Email</StyledTableCell>
                            <TableCell style={{ width: '90%' }}>
                                {editMode ? (
                                    <TextField
                                        name="email"
                                        label="Email"
                                        value={clientDetails.email}
                                        onChange={(e) => handleClientDetailsChange('email', e.target.value)}
                                        fullWidth
                                        size='small'
                                        required
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                ) : (
                                    clientDetails.email
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell style={{ width: '10%' }}>Dossier Id</StyledTableCell>
                            <TableCell style={{ width: '90%' }}>
                                {editMode ? (
                                    <TextField
                                        name="dossierId"
                                        label="Dossier Id"
                                        value={clientDetails.dossierId}
                                        onChange={(e) => handleClientDetailsChange('dossierId', e.target.value)}
                                        fullWidth
                                        size='small'
                                        required
                                        error={!!errors.dossierId}
                                        helperText={errors.dossierId}
                                    />
                                ) : (
                                    clientDetails.dossierId
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell style={{ width: '10%' }}>High Risk</StyledTableCell>
                            <TableCell style={{ width: '90%' }}>
                                {editMode ? (
                                    <TextField
                                        name="highRisk"
                                        label="High Risk"
                                        value={clientDetails.highRisk}
                                        onChange={(e) => handleClientDetailsChange('highRisk', e.target.value)}
                                        fullWidth
                                        size='small'
                                        select
                                    >
                                        <MenuItem value={false}>No</MenuItem>
                                        <MenuItem value={true}>Yes</MenuItem>
                                    </TextField>
                                ) : (
                                    clientDetails.highRisk ? 'Yes' : 'No'
                                )}
                            </TableCell>
                        </TableRow>
                        {clientDetails.type === 0 && ClientDetailsHelper.GetPrivateRows(editMode, clientDetails, handleClientDetailsChange, errors)}
                        {clientDetails.type === 1 && ClientDetailsHelper.GetLegalRows(editMode, clientDetails, handleClientDetailsChange, errors)}
                    </TableBody>
                </Table>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="agreements">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={4}>Type</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Conclusion Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Termination Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Fee, %</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {agreements && agreements.map((agreement) => (
                                <StyledTableBodyRow key={agreement.id}
                                                    onClick={() => editAgreement(agreement.id)}>
                                    <StyledTableCell>{GetEnumValueName('brokerageAgreementType', agreement.type)}</StyledTableCell>
                                    <StyledTableCell>{dayjs(agreement.conclusionDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{agreement.terminationDate === null ? '-' : dayjs(agreement.terminationDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{agreement.feeInPercent}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="limits">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={9}>Asset</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Brokerage Agreement</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Created Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Extension Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Extension Volume</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Total Volume</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Reminder Volume</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Extension Number</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Source</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {limits && limits.map((limit) => (
                                <StyledTableBodyRow key={limit.id}
                                                    onClick={() => editLimit(limit.id)}>
                                    <StyledTableCell>{GetLookupValueName('asset', limit.assetId)}</StyledTableCell>
                                    <StyledTableCell>{GetBrokerageAgreementTitle(agreements.find(e => e.id === limit.brokerageAgreementId))}</StyledTableCell>
                                    <StyledTableCell>{dayjs(limit.createdDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{dayjs(limit.extensionDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{limit.extensionVolume}</StyledTableCell>
                                    <StyledTableCell>{limit.totalVolume}</StyledTableCell>
                                    <StyledTableCell>{limit.reminderVolume}</StyledTableCell>
                                    <StyledTableCell>{limit.extensionNumber}</StyledTableCell>
                                    <StyledTableCell>{limit.source}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            {clientDetails.type === 1 && (
                <StyledTabPanel value={activeTab} index="company-members">
                    <StyledTableContainer component={Paper}>
                        <StyledTable>
                            <TableHead>
                                <StyledTableHeadRow>
                                    <StyledTableCellHeader columncount={3}>Type</StyledTableCellHeader>
                                    <StyledTableCellHeader columncount={3}>Member</StyledTableCellHeader>
                                    <StyledTableCellHeader columncount={3}>Shares, %</StyledTableCellHeader>
                                </StyledTableHeadRow>
                            </TableHead>
                            <TableBody>
                                {companyMembers && companyMembers.map((member) => (
                                    <StyledTableBodyRow key={member.id}
                                                        onClick={() => editCompanyMember(member.id)}>
                                        <StyledTableCell>{GetEnumValueName('companyMemberType', member.type)}</StyledTableCell>
                                        <StyledTableCell>{GetClientViewName(clients.find(e => e.id === member.memberId))}</StyledTableCell>
                                        <StyledTableCell>{member.sharesInPercent ?? '-'}</StyledTableCell>
                                    </StyledTableBodyRow>
                                ))}
                            </TableBody>
                        </StyledTable>
                    </StyledTableContainer>
                </StyledTabPanel>
            )}
            <StyledTabPanel value={activeTab} index="wallets">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={4}>Asset</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Owner</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Address</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Multisig</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {wallets && wallets.map((wallet) => (
                                <StyledTableBodyRow key={wallet.id}
                                                    onClick={() => editWallet(wallet.id)}>
                                    <StyledTableCell>{GetLookupValueName('asset', wallet.assetId)}</StyledTableCell>
                                    <StyledTableCell>{wallet.ownerId === Number(process.env.REACT_APP_NEWCENT_CONTACT_ID) ? 'Newcent' : 'Client'}</StyledTableCell>
                                    <StyledTableCell>{wallet.address}</StyledTableCell>
                                    <StyledTableCell>{wallet.isMultisig ? 'Yes' : 'No'}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="bankAccounts">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={4}>Bank</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Bank Country</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Asset</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={4}>Iban</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {bankAccounts && bankAccounts.map((bankAccount) => (
                                <StyledTableBodyRow key={bankAccount.id}
                                                    onClick={() => editBankAccount(bankAccount.id)}>
                                    <StyledTableCell>{GetLookupValueName('bank', bankAccount.bankId)}</StyledTableCell>
                                    <StyledTableCell>{GetLookupValueName('country', GetLookupValue('bank', bankAccount.bankId).countryId)}</StyledTableCell>
                                    <StyledTableCell>{GetLookupValueName('asset', bankAccount.assetId)}</StyledTableCell>
                                    <StyledTableCell>{bankAccount.iban}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="transfers">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={9}>Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Type</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>From</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Amount From</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>To</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Amount To</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Fee</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Fee Asset</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}></StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {transfers && transfers.map((transfer) => (
                                <StyledTableBodyRow key={transfer.id}
                                                    onClick={() => editTransfer(transfer.id)}>
                                    <StyledTableCell>{dayjs(transfer.date).toString()}</StyledTableCell>
                                    <StyledTableCell>{GetEnumValueName('transferType', transfer.type)}</StyledTableCell>
                                    <StyledTableCell>{TransferHelper.GetTransferSide(wallets, bankAccounts, transfer.type, transfer.fromId)}</StyledTableCell>
                                    <StyledTableCell>{transfer.amountFrom}</StyledTableCell>
                                    <StyledTableCell>{TransferHelper.GetTransferSide(wallets, bankAccounts, transfer.type, transfer.toId)}</StyledTableCell>
                                    <StyledTableCell>{transfer.amountTo}</StyledTableCell>
                                    <StyledTableCell>{transfer.fee}</StyledTableCell>
                                    <StyledTableCell>{GetLookupValueName('asset', transfer.feeAssetId)}</StyledTableCell>
                                    <StyledTableCell>
                                        <StyledButtonOrange
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                ConfirmationDownloader.GenerateTransferConfirmation(transfer.id, snackbar, setSnackbar);
                                            }}>
                                            Confirmation
                                        </StyledButtonOrange>
                                    </StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="orders">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={9}>Created Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Expiration Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Action</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Type</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Status</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Crypto Amount Requested</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Requested Price</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Crypto Amount Executed</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Executed Price</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {orders && orders.map((order) => (
                                <StyledTableBodyRow key={order.id}
                                                    onClick={() => editOrder(order.id)}>
                                    <StyledTableCell>{dayjs(order.createdDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{dayjs(order.expirationDate).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{GetEnumValueName('orderAction', order.action)}</StyledTableCell>
                                    <StyledTableCell>{GetEnumValueName('orderType', order.type)}</StyledTableCell>
                                    <StyledTableCell>{GetEnumValueName('orderStatus', order.status)}</StyledTableCell>
                                    <StyledTableCell>{GetCryptoAmountRequestedAsSting(order)}</StyledTableCell>
                                    <StyledTableCell>{GetRequestedPricesSting(order)}</StyledTableCell>
                                    <StyledTableCell>{GetCryptoAmountExecutedAsSting(order)}</StyledTableCell>
                                    <StyledTableCell>{GetExecutedPricesSting(order)}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="trades">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={9}>Date</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Order</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Action</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Crypto Amount</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Price</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Brokerage Fee</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Transaction Fee</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}>Fee Asset</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={9}></StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {trades && trades.map((trade) => (
                                <StyledTableBodyRow key={trade.id}
                                                    onClick={() => editTrade(trade.id)}>
                                    <StyledTableCell>{dayjs(trade.date).toDate().toDateString()}</StyledTableCell>
                                    <StyledTableCell>{GetOrderTitle(orders.find(e => e.id === trade.orderId))}</StyledTableCell>
                                    <StyledTableCell>{GetEnumValueName('orderAction', trade.action)}</StyledTableCell>
                                    <StyledTableCell>{trade.cryptoAmount}</StyledTableCell>
                                    <StyledTableCell>{trade.price}</StyledTableCell>
                                    <StyledTableCell>{trade.brokerageFee}</StyledTableCell>
                                    <StyledTableCell>{trade.transactionFee}</StyledTableCell>
                                    <StyledTableCell>{GetLookupValueName('asset', trade.feeAssetId)}</StyledTableCell>
                                    <StyledTableCell>
                                        <StyledButtonOrange
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                ConfirmationDownloader.GenerateTradeConfirmation(trade.id, snackbar, setSnackbar);
                                            }}>
                                            Confirmation
                                        </StyledButtonOrange>
                                    </StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="files">
                <ManagerFilesPage clientId={clientId} ref={filesComponentRef}/>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="aml">
                <AmlPage clientId={clientId} ref={amlComponentRef}/>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="chainalysis">
                <StyledTableContainer component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <StyledTableHeadRow>
                                <StyledTableCellHeader columncount={5}>Alert Level</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={5}>Service</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={5}>External Id</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={5}>Alert Amount</StyledTableCellHeader>
                                <StyledTableCellHeader columncount={5}>ExposureType</StyledTableCellHeader>
                            </StyledTableHeadRow>
                        </TableHead>
                        <TableBody>
                            {chainalysisAlerts && chainalysisAlerts.map((alert) => (
                                <StyledTableBodyRow key={alert.id}>
                                    <StyledTableCell>{alert.alertLevel}</StyledTableCell>
                                    <StyledTableCell>{alert.service}</StyledTableCell>
                                    <StyledTableCell>{alert.externalId}</StyledTableCell>
                                    <StyledTableCell>{alert.alertAmount}</StyledTableCell>
                                    <StyledTableCell>{alert.exposureType}</StyledTableCell>
                                </StyledTableBodyRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>
            </StyledTabPanel>
            <StyledTabPanel value={activeTab} index="balance">
                <BalanceComponent clientId={clientId}/>
            </StyledTabPanel>
        </Container>
    );
};

export default ClientPage;
